import React, { useState, useEffect } from "react";
import SEO from "./SEO";
import { FaHamburger, FaCoffee, FaIceCream, FaCocktail, FaPizzaSlice, FaDrumstickBite, FaFish, FaLeaf } from "react-icons/fa";
import { motion } from "framer-motion";

export default function Cafe() {
  const [currentImage, setCurrentImage] = useState({});

  const cafeSections = [
    {
      id: 1,
      title: "Our Foods",
      images: ["/food1.jpg", "/food2.jpg", "/food3.webp"],
      items: [
        { en: "National Dishes", am: "ብሔራዊ ምግቦች", icon: <FaLeaf /> },
        { en: "Chicken", am: "ዶሮ", icon: <FaDrumstickBite /> },
        { en: "Fish", am: "ዓሣ", icon: <FaFish /> },
        { en: "Soup", am: "ሾርባ", icon: <FaLeaf /> },
        { en: "Salad", am: "ሰላዳ", icon: <FaLeaf /> },
        { en: "Burgers", am: "በርገር", icon: <FaHamburger /> },
        { en: "Pizza", am: "ፒዛ", icon: <FaPizzaSlice /> },
      ],
    },
    {
      id: 2,
      title: "Drinks",
      images: ["/cakee1.jpg", "/juse1.jpg"],
      items: [
        { en: "Coffee & Tea", am: "ቡና እና ሻይ", icon: <FaCoffee /> },
        { en: "Juices", am: "ጁስ", icon: <FaCocktail /> },
        { en: "Smoothies", am: "ስሙውዲ", icon: <FaCocktail /> },
        { en: "Cocktails", am: "ኮክቴል", icon: <FaCocktail /> },
        { en: "Soft Drinks", am: "ሶፍት ድሪንክ", icon: <FaCoffee /> },
      ],
    },
    {
      id: 3,
      title: "Desserts & Cakes",
      images: ["/cakee2.jpg", "/juse2.webp"],
      items: [
        { en: "Cakes", am: "ኬክ", icon: <FaIceCream /> },
        { en: "Cupcakes", am: "ኩፕኬክ", icon: <FaIceCream /> },
        { en: "Pastries", am: "ፓስትሪ", icon: <FaIceCream /> },
        { en: "Ice Cream", am: "አይስ ክሬም", icon: <FaIceCream /> },
        { en: "Chocolate Desserts", am: "ቸኮሌት ዳዘርት", icon: <FaIceCream /> },
      ],
    },
  ];

  // Image cycling effect
  useEffect(() => {
    const interval = setInterval(() => {
      const newCurrent = {};
      cafeSections.forEach((section) => {
        newCurrent[section.id] =
          currentImage[section.id] !== undefined
            ? (currentImage[section.id] + 1) % section.images.length
            : 0;
      });
      setCurrentImage(newCurrent);
    }, 3000);
    return () => clearInterval(interval);
  }, [currentImage, cafeSections]);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <SEO
        title="Café | Ferej Hotel Sol - Harar, Ethiopia"
        description="Enjoy traditional Ethiopian coffee and delicious meals at Ferej Hotel Sol Café, Harar’s finest dining spot."
        keywords="Ferej Hotel Café, restaurant in Harar, coffee in Harar, best food in Harar"
        url="https://ferejhotel.com/cafe"
      />

      <section
        id="cafe"
        className="py-24 font-[Poppins]"
        style={{
          background:
            "linear-gradient(135deg, #fffdf5 0%, #fff8e1 50%, #fff1c4 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-yellow-700 mb-10 font-[Playfair_Display] drop-shadow-md">
            Cafe & Dining
          </h2>
          <p className="text-xl text-gray-700 mb-16 max-w-3xl mx-auto leading-relaxed">
            Explore our delicious foods, refreshing drinks, and delightful desserts.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {cafeSections.map((section, index) => (
              <motion.div
                key={section.id}
                className="bg-gradient-to-br from-[#fff7dc] via-[#ffe084] to-[#ffd700] rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-yellow-400/50 hover:shadow-lg"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={cardVariants}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Carousel Image */}
                <div className="h-60 w-full overflow-hidden relative rounded-t-3xl">
                  <img
                    src={section.images[currentImage[section.id] || 0]}
                    alt={section.title}
                    className="h-60 w-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>

                <div className="p-8 text-left">
                  <h3 className="text-2xl font-semibold text-yellow-800 mb-4 font-[Playfair_Display] flex items-center gap-2">
                    {section.title}
                  </h3>
                  <ul className="space-y-3 text-gray-800 text-lg">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="text-yellow-700 text-xl">{item.icon}</span>
                        <span>
                          {item.en}{" "}
                          <span className="text-gray-500 italic text-sm">{item.am}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Poppins:wght@400;500;600&display=swap');
          `}
        </style>
      </section>
    </>
  );
}
